import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
interface Testimonial {
  initials: string;
  name: string;
  location: string;
  text: string;
  color: string;
  pack?: string;
}
interface TestimonialsProps {
  testimonials: Testimonial[];
  title?: string;
  className?: string;
}
export function Testimonials({
  testimonials,
  title,
  className = ''
}: TestimonialsProps) {
  return <section className={`py-16 bg-zinc-900 ${className}`} data-id="element-308">
      <div className="container mx-auto px-4" data-id="element-309">
        {title && <h2 className="font-bold text-center mb-12 text-white text-[16px] md:text-2xl" data-id="element-310">
            {title}
          </h2>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-311">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-black/70 p-6 rounded-2xl shadow-lg border border-violet-900/40 flex flex-col h-full" data-id="element-312">
              <div className="flex items-center gap-1 mb-4" data-id="element-313">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-violet-400 fill-violet-400" data-id="element-314" />)}
              </div>

              <p className="text-zinc-100 italic mb-6 flex-grow" data-id="element-315">"{t.text}"</p>

              <div className="flex items-center gap-3 mt-auto pt-4 border-t border-violet-900/30" data-id="element-316">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${t.color}`} data-id="element-317">
                  {t.initials}
                </div>
                <div data-id="element-318">
                  <p className="font-bold text-white text-sm" data-id="element-319">{t.name}</p>
                  <p className="text-xs text-violet-200" data-id="element-320">{t.location}</p>
                  {t.pack && <p className="text-xs font-medium text-violet-300 mt-0.5" data-id="element-321">
                      {t.pack}
                    </p>}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export const initialTestimonials = [{
  initials: 'MC',
  name: 'María C.',
  location: 'Maestra de grado · Córdoba',
  text: 'Llevaba años escribiendo siempre las mismas frases en los boletines. Leí el Tomo 1 y el Tomo 3 antes del primer cierre y por primera vez entendí cómo redactar observaciones que realmente describen a cada alumno. No era falta de vocación. Era que no tenía las herramientas.',
  color: 'bg-violet-600'
}, {
  initials: 'RL',
  name: 'Romina L.',
  location: 'Docente de primaria · Rosario',
  text: 'El banco de 500 frases me cambió completamente. Antes escribía lo mismo para todos. Ahora tengo la observación exacta para el alumno destacado, el que está en proceso y el que necesita apoyo. En el último cierre de trimestre terminé todo en una tarde.',
  color: 'bg-purple-600'
}, {
  initials: 'FV',
  name: 'Fabiana V.',
  location: 'Docente de secundaria · Mendoza',
  text: 'Soy docente hace 15 años y nunca tuve un sistema para las actas y las reuniones de padres. El Tomo 6 me dio todos los modelos que necesitaba. Ahora llego a cada reunión con todo documentado y con un guion claro. Eso me da una seguridad que antes no tenía.',
  color: 'bg-fuchsia-600'
}];
export const moreTestimonials = [{
  initials: 'JM',
  name: 'Julia M.',
  location: '🏙️ Buenos Aires',
  pack: 'MegaPack Informes Docentes',
  text: 'Antes del cierre del primer trimestre pasé tres fines de semana escribiendo boletines. Con el MegaPack entendí que el problema no era mi capacidad para describir a mis alumnos: era que no tenía las herramientas. Ahora cierro todos los informes en una tarde.',
  color: 'bg-emerald-600'
}, {
  initials: 'DS',
  name: 'Diana S.',
  location: '🏙️ Santa Fe',
  pack: 'MegaPack Informes Docentes',
  text: 'El Tomo 6 de Actas me cambió completamente cómo manejo las reuniones difíciles. Antes de tener el modelo, cada reunión con una familia conflictiva me generaba ansiedad. Ahora llego con el guion preparado, el acta en blanco y los datos del registro. Eso me da una seguridad enorme.',
  color: 'bg-violet-700'
}, {
  initials: 'CP',
  name: 'Claudia P.',
  location: '🏙️ Tucumán',
  pack: 'MegaPack + Planificador Sistémico',
  text: 'El Planificador Sistémico me dio el Registro Diario que siempre quise tener. Ahora anoto 5 minutos al final de cada clase y cuando llega el cierre, los informes se escriben solos. No es exageración: tengo todos los datos ahí.',
  color: 'bg-purple-700'
}, {
  initials: 'AR',
  name: 'Adriana R.',
  location: '🏙️ La Plata',
  pack: 'MegaPack Informes Docentes',
  text: 'El Diccionario de Sinónimos del Bonus 3 debería ser obligatorio para todo docente. Llevaba años escribiendo siempre las mismas palabras. Ahora mis informes tienen variedad, precisión técnica y un nivel de profesionalismo que antes no tenía.',
  color: 'bg-fuchsia-700'
}, {
  initials: 'MG',
  name: 'Marcela G.',
  location: '🏙️ Rosario',
  pack: 'MegaPack + Planificador Sistémico',
  text: 'Soy directora de una escuela primaria y recomendé el MegaPack a todo mi equipo docente. La mejora en la calidad de los informes fue inmediata y visible. El vocabulario técnico, la coherencia entre calificaciones y observaciones, y la documentación de actas mejoraron notablemente.',
  color: 'bg-indigo-700'
}];